import { ThreeEvent } from '@react-three/fiber';
import {
  AirHandlerTypeCodeType,
  ConfigurationType,
  GridSpaceType,
  LevelType,
  LineSetType
} from 'data/types';
import { use3DRightClick } from 'hooks/use-3d-right-click';
import { useMemo, useState } from 'react';
import { MeshStandardMaterial, Quaternion, Vector3 } from 'three';
import { LINESET_DEFAULT_COLOR } from 'utils/3d-lineset-color';
import {
  computeLineSetGeometry,
  LINESET_AIR_HANDLER_OFFSET
} from 'utils/3d-lineset-geometry';
import {
  getOpacityMaterialProps,
  getOpacityRenderOrder
} from 'utils/3d-opacity';

type LineSet3DProps = {
  lineset: LineSetType;
  configuration: ConfigurationType | undefined;
  structure?: LevelType[];
  yOffset?: number;
  opacity?: number;
  color?: number;
  airHandlerTypeCode?: string;
  spaces?: GridSpaceType[];
  interactive?: boolean;
  onSegmentContextMenu?: (
    event: ThreeEvent<PointerEvent>,
    segmentIndex: number
  ) => void;
};

function CylinderSegment({
  start,
  end,
  radius,
  opacity,
  isVertical = false,
  color = LINESET_DEFAULT_COLOR
}: {
  start: [number, number, number];
  end: [number, number, number];
  radius: number;
  opacity: number;
  isVertical?: boolean;
  color?: number;
}) {
  const material = useMemo(
    () =>
      new MeshStandardMaterial({
        color,
        ...getOpacityMaterialProps(opacity)
      }),
    [opacity, color]
  );

  const [x1, y1, z1] = start;
  const [x2, y2, z2] = end;

  const dx = x2 - x1;
  const dy = y2 - y1;
  const dz = z2 - z1;
  const length = Math.sqrt(dx * dx + dy * dy + dz * dz);

  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2;
  const midZ = (z1 + z2) / 2;

  const quaternion = useMemo(() => {
    if (isVertical) {
      return new Quaternion();
    }

    const totalLength = Math.sqrt(dx * dx + dy * dy + dz * dz);

    if (totalLength < 0.001) {
      return new Quaternion();
    }

    const direction = new Vector3(dx, dy, dz).normalize();
    const up = new Vector3(0, 1, 0);
    const quat = new Quaternion();

    quat.setFromUnitVectors(up, direction);
    return quat;
  }, [isVertical, dx, dy, dz]);

  return (
    <group position={[midX, midY, midZ]}>
      <mesh
        quaternion={quaternion}
        material={material}
        position={[0, 0, 0]}
        renderOrder={getOpacityRenderOrder(opacity)}
      >
        <cylinderGeometry args={[radius, radius, length, 8]} />
      </mesh>
    </group>
  );
}

function SegmentHitArea({
  start,
  end,
  radius,
  onContextMenu,
  onPointerOver,
  onPointerOut
}: {
  start: [number, number, number];
  end: [number, number, number];
  radius: number;
  onContextMenu: (event: ThreeEvent<PointerEvent>) => void;
  onPointerOver: () => void;
  onPointerOut: () => void;
}) {
  const rightClick = use3DRightClick(onContextMenu);
  const [x1, y1, z1] = start;
  const [x2, y2, z2] = end;

  const dx = x2 - x1;
  const dy = y2 - y1;
  const dz = z2 - z1;
  const length = Math.sqrt(dx * dx + dy * dy + dz * dz);

  const quaternion = useMemo(() => {
    if (length < 0.001) {
      return new Quaternion();
    }
    const direction = new Vector3(dx, dy, dz).normalize();
    return new Quaternion().setFromUnitVectors(new Vector3(0, 1, 0), direction);
  }, [dx, dy, dz, length]);

  if (length < 0.001) return null;

  return (
    <mesh
      position={[(x1 + x2) / 2, (y1 + y2) / 2, (z1 + z2) / 2]}
      quaternion={quaternion}
      onPointerDown={rightClick.onPointerDown}
      onPointerUp={rightClick.onPointerUp}
      onPointerOver={onPointerOver}
      onPointerOut={onPointerOut}
    >
      <cylinderGeometry args={[radius, radius, length, 6]} />
      <meshBasicMaterial transparent opacity={0} depthWrite={false} />
    </mesh>
  );
}

export default function LineSet3D({
  lineset,
  configuration,
  structure = [],
  yOffset = 0,
  opacity = 1.0,
  color = LINESET_DEFAULT_COLOR,
  airHandlerTypeCode: propAirHandlerTypeCode,
  spaces = [],
  interactive = false,
  onSegmentContextMenu
}: LineSet3DProps) {
  const cylinderRadius = 0.02;
  const hitRadius = 0.15;
  const [hoveredSegmentIndex, setHoveredSegmentIndex] = useState<number | null>(
    null
  );

  const geometry = useMemo(
    () =>
      computeLineSetGeometry({
        lineset,
        configuration,
        structure,
        yOffset,
        airHandlerTypeCode: propAirHandlerTypeCode as
          | AirHandlerTypeCodeType
          | undefined,
        spaces
      }),
    [lineset, configuration, structure, yOffset, propAirHandlerTypeCode, spaces]
  );

  if (lineset.points.length < 2) {
    return null;
  }

  const { points, segments } = geometry;

  const firstPointGeometry = points[0];
  const lastPointGeometry = points[points.length - 1];
  const firstPointOverlapsCondenser =
    firstPointGeometry.overlappingCondenser !== undefined;
  const lastPointOverlapsCondenser =
    lastPointGeometry.overlappingCondenser !== undefined;

  return (
    <group>
      {segments.map((segment, index) => {
        const point = lineset.points[index];
        let x1 = segment.startX;
        let z1 = segment.startZ;
        let x2 = segment.endX;
        let z2 = segment.endZ;
        let y1 = segment.startY;
        let y2 = segment.endY;

        const isFirstSegment = index === 0;
        const isLastSegment = index === segments.length - 1;

        if (isFirstSegment && !firstPointOverlapsCondenser) {
          if (firstPointGeometry.overlappingAirHandler) {
            x1 = firstPointGeometry.x;
            y1 = firstPointGeometry.y;
            z1 = firstPointGeometry.z;
          } else {
            const dx = x2 - x1;
            const dz = z2 - z1;
            const len = Math.sqrt(dx * dx + dz * dz);
            if (len > 0) {
              x1 += (dx / len) * LINESET_AIR_HANDLER_OFFSET;
              z1 += (dz / len) * LINESET_AIR_HANDLER_OFFSET;
            }
          }
        }

        if (isLastSegment && !lastPointOverlapsCondenser) {
          if (lastPointGeometry.overlappingAirHandler) {
            x2 = lastPointGeometry.x;
            y2 = lastPointGeometry.y;
            z2 = lastPointGeometry.z;
          } else {
            const dx = x1 - x2;
            const dz = z1 - z2;
            const len = Math.sqrt(dx * dx + dz * dz);
            if (len > 0) {
              x2 += (dx / len) * LINESET_AIR_HANDLER_OFFSET;
              z2 += (dz / len) * LINESET_AIR_HANDLER_OFFSET;
            }
          }
        }

        const firstPointOverlaps =
          isFirstSegment && firstPointOverlapsCondenser;
        const lastPointOverlaps = isLastSegment && lastPointOverlapsCondenser;
        const isCondenserSegment = firstPointOverlaps || lastPointOverlaps;

        const nextSegment = segments[index + 1];
        const isTransitionStart =
          nextSegment !== undefined &&
          nextSegment.riserFromY === undefined &&
          Math.abs(segment.endY - nextSegment.startY) > 0.001;

        let condenserY = 0;
        if (firstPointOverlaps) {
          condenserY = firstPointGeometry.y;
        } else if (lastPointOverlaps) {
          condenserY = lastPointGeometry.y;
        }

        const segmentStart: [number, number, number] = isCondenserSegment
          ? [
              firstPointOverlaps ? firstPointGeometry.x : x1,
              condenserY,
              firstPointOverlaps ? firstPointGeometry.z : z1
            ]
          : [x1, y1, z1];
        const segmentEnd: [number, number, number] = isCondenserSegment
          ? [
              lastPointOverlaps ? lastPointGeometry.x : x2,
              condenserY,
              lastPointOverlaps ? lastPointGeometry.z : z2
            ]
          : [x2, y2, z2];

        const isHovered = interactive && hoveredSegmentIndex === index;

        return (
          <group key={point.uniqueId}>
            <CylinderSegment
              start={segmentStart}
              end={segmentEnd}
              radius={cylinderRadius}
              opacity={opacity}
              color={color}
            />
            {isHovered && (
              <CylinderSegment
                start={segmentStart}
                end={segmentEnd}
                radius={cylinderRadius * 3}
                opacity={0.3}
                color={0x666666}
              />
            )}
            {interactive && onSegmentContextMenu && (
              <SegmentHitArea
                start={segmentStart}
                end={segmentEnd}
                radius={hitRadius}
                onContextMenu={(event) => onSegmentContextMenu(event, index)}
                onPointerOver={() => setHoveredSegmentIndex(index)}
                onPointerOut={() =>
                  setHoveredSegmentIndex((prev) =>
                    prev === index ? null : prev
                  )
                }
              />
            )}
            {firstPointOverlaps && (
              <CylinderSegment
                start={[x2, condenserY, z2]}
                end={[x2, y2, z2]}
                radius={cylinderRadius}
                opacity={opacity}
                color={color}
                isVertical
              />
            )}
            {lastPointOverlaps && (
              <CylinderSegment
                start={[x1, condenserY, z1]}
                end={[x1, y1, z1]}
                radius={cylinderRadius}
                opacity={opacity}
                color={color}
                isVertical
              />
            )}
            {isTransitionStart && (
              <CylinderSegment
                start={[x2, segment.endY, z2]}
                end={[x2, segments[index + 1].startY, z2]}
                radius={cylinderRadius * 1.5}
                opacity={opacity}
                color={color}
                isVertical
              />
            )}
            {segment.riserFromY !== undefined && (
              <CylinderSegment
                start={[segment.startX, segment.riserFromY, segment.startZ]}
                end={[segment.startX, segment.startY, segment.startZ]}
                radius={cylinderRadius * 1.5}
                opacity={opacity}
                color={color}
                isVertical
              />
            )}
          </group>
        );
      })}
    </group>
  );
}
